import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";
import { IDepartments, IDepartmentTree, IProduct } from "redux/reducers/models/product";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import ItemSalesHistoryTable from "../tables/ItemSalesHistoryTable";
import { Select } from "components/select/index";
import { useTranslation } from "react-i18next";
import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import moment from "moment";
import { faBarsFilter, faXmark } from "@fortawesome/pro-regular-svg-icons";
import FilterDrawer from "../components/filterDrawer/FilterDrawer";
import useModal from "hooks/modals/useModal";
import { ButtonNew } from "components/buttonNew";
import Portal from "elements/Portal";
import FilterTags from "../components/filterTags/FilterTags";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  products: IProduct[];
  productQuery: string;
  productSearching: boolean;
  selectedProducts: IProduct[];
  departmentTree: IDepartmentTree[];
  allDepartments: IDepartments[];
  email: string;
  queue_after_days: number;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

interface IFilterState {
  department: number[];
  category: number[];
  subcategory: number[];
}

export default function ItemSalesHistory() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const GetReportRun = useReportRun();
  const dispatch = useAppDispatch();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const { t } = useTranslation();
  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    products: null,
    productQuery: "",
    productSearching: false,
    allDepartments: null,
    departmentTree: null,
    selectedProducts: [],
    email: "",
    queue_after_days: 14,
  });

  const filterKeys: (keyof IFilterState)[] = ["department", "category", "subcategory"];

  const [filterState, setFilterState] = useState<IFilterState>({
    department: [],
    category: [],
    subcategory: [],
  });

  const { state: filterModal, updateModal: updateFilterModal, closeModal: closeFilterModal } = useModal();

  // For department/cat/subcat tags
  const departmentMap = React.useMemo(() => {
    return new Map((state.allDepartments ?? []).map(item => [item.id, item]));
  }, [state.allDepartments]);

  // For displaying filter tags
  const filterDisplayState = {
    department: (filterState.department ?? []).map(id => departmentMap.get(id)?.title ?? String(id)),
    category: (filterState.category ?? []).map(id => departmentMap.get(id)?.title ?? String(id)),
    subcategory: (filterState.subcategory ?? []).map(id => departmentMap.get(id)?.title ?? String(id)),
  };

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
    void loadDepartments();
  }, []);

  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProducts(source.token);
    return () => {
      source.cancel();
    };
  }, [state.productQuery]);

  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];
    const sortedConfigurations = [...report.configurations].sort((prev, next) => prev.position - next.position);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      configurations: sortedConfigurations,
    }));
  }

  async function loadDepartments() {
    const departmentRes = await GetDepartments(null, true);
    if (departmentRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading facility departments"));
      return;
    }

    const departments = departmentRes.data.filter(obj => obj.type === "department");
    const categories = departmentRes.data.filter(obj => obj.type === "category");
    const subCategories = departmentRes.data.filter(obj => obj.type === "subcategory");

    const mapById = new Map();

    [...departments, ...categories, ...subCategories].forEach(item => {
      mapById.set(item.id, { ...item, children: [] });
    });

    const tree: any[] = [];

    mapById.forEach(item => {
      if (item.parent_id) {
        const parent = mapById.get(item.parent_id);
        if (parent) {
          parent.children.push(item);
        }
      } else {
        tree.push(item);
      }
    });

    setState(prevState => ({
      ...prevState,
      departmentTree: tree,
      allDepartments: departmentRes.data,
    }));
  }

  async function loadProducts(token?: CancelToken) {
    setState(prev => ({
      ...prev,
      productSearching: true,
    }));
    const productRes = await GetProduct({ search: state.productQuery }, false, token);

    if (productRes.status !== StatusCode.OK) {
      return;
    }

    setState(prev => ({
      ...prev,
      products: productRes.data,
      productSearching: false,
    }));
  }

  async function getReport(email?: string) {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        product_ids: state.selectedProducts?.map(p => p.id),
        department_ids: filterState?.department,
        category_ids: filterState?.category,
        subcategory_ids: filterState?.subcategory,
        email,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(reportRes.message));
      return;
    }

    if (reportRes.message) {
      dispatch(showSuccess(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {
    if (state.selectedProducts?.length === 0) {
      dispatch(showError("No Product Selected."));
      return;
    }

    const segmentsRes = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        date_range: state.date_range,
        product_ids: state.selectedProducts?.map(p => p.id),
        department_ids: filterState?.department,
        category_ids: filterState?.category,
        subcategory_ids: filterState?.subcategory,
        email,
      },
      true,
    );

    if (segmentsRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.item_sales_history.001")));
      return;
    }

    if (!email) {
      downloadCsv(segmentsRes.data as string, reportFilename() + ".csv");
    } else {
      dispatch(showSuccess(segmentsRes.message));
    }
  }

  async function getPrint(email: string) {
    if (state.selectedProducts?.length === 0) {
      dispatch(showError("No Product Selected."));
      return;
    }

    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        date_range: state.date_range,
        product_ids: state.selectedProducts?.map(p => p.id),
        department_ids: filterState?.department,
        category_ids: filterState?.category,
        subcategory_ids: filterState?.subcategory,
        email,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.item_sales_history.002")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.item_sales_history.003"),
    action: () => {
      if (reportShouldQueue()) {
        dispatch(
          showSuccess(
            t("Potential large report detected."),
            10000,
            t("An email is required to send the report to upon completion."),
          ),
        );

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`.replace(/ /g, "_").toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), "days") >= state.queue_after_days;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload("");
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint("");
    }
  }

  function handleProductSelect(product: IProduct) {
    setState(prevState => {
      const isSelected = prevState.selectedProducts.some(p => p.id === product.id);

      if (isSelected) {
        return {
          ...prevState,
          selectedProducts: prevState.selectedProducts.filter(p => p.id !== product.id),
        };
      }

      return { ...prevState, selectedProducts: [...prevState.selectedProducts, product] };
    });
  }

  function handleProductRemove(product: IProduct) {
    setState(prevState => ({
      ...prevState,
      selectedProducts: prevState.selectedProducts.filter(p => p.id !== product.id),
    }));
  }

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked, id } = e.target;

    // Filter Key (Department/Category/Subcategory/etc)
    const key = id as keyof IFilterState;
    setFilterState(prevState => {
      const current: number[] = prevState[key] ?? [];
      const numericValue = Number(value);

      let updated = [];
      if (key === "department") {
        const department = state?.departmentTree?.find(dept => dept.id === numericValue);

        if (!department) {
          return prevState;
        }

        const selections = collectFilters(department, {
          department: [],
          category: [],
          subcategory: [],
        });

        return {
          ...prevState,
          department: checked
            ? [...prevState.department, ...selections.department]
            : prevState.department.filter(id => !selections.department.includes(id)),

          category: checked
            ? [...prevState.category, ...selections.category]
            : prevState.category.filter(id => !selections.category.includes(id)),

          subcategory: checked
            ? [...prevState.subcategory, ...selections.subcategory]
            : prevState.subcategory.filter(id => !selections.subcategory.includes(id)),
        };
      } else {
        updated = checked ? [...current, numericValue] : current.filter(valueId => valueId !== numericValue);
      }

      return { ...prevState, [id]: updated };
    });
  }

  function collectFilters(node: any, result: IFilterState): IFilterState {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    result[node.type as keyof IFilterState].push(node.id);

    node.children?.forEach((child: any) => collectFilters(child, result));

    return result;
  }

  function handleClearAllFilters() {
    setFilterState(prev => ({ ...prev, category: [], subcategory: [], department: [] }));
  }

  function clearSelectedFilter(filterKey: string) {
    setFilterState(prev => ({ ...prev, [filterKey]: [] }));
  }

  return (
    <Page
      title={t("secure.facility.reports.pages.item_sales_history.004")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("secure.facility.reports.pages.item_sales_history.005"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.reports.pages.item_sales_history.006"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("secure.facility.reports.pages.item_sales_history.007"),
              handler: handlePrint,
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[
        { prefix: true, label: t("secure.facility.reports.pages.item_sales_history.008"), url: "/admin/report" },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
            <div className="flex flex-row">
              <Select
                showSearch
                onSearch={(query: string) => setState(prevState => ({ ...prevState, productQuery: query }))}
                onChange={(_, product) => handleProductSelect(product as IProduct)}
                placeholder={t("secure.facility.reports.pages.item_sales_history.009")}
                allowClear
                searchValue={state.productQuery}
                showDropDownOnFocus={true}
                searching={state.productSearching}
              >
                {state.products?.map((product, index) => (
                  <Select.Option key={index} value={product.id} name={product.title} extraValues={product}>
                    {`${product.title}${product.subtitle ? " - " + product.subtitle : ""}`}
                  </Select.Option>
                ))}
              </Select>
            </div>
            {state.selectedProducts.length > 0 && (
              <div className="flex justify-start gap-1">
                {state.selectedProducts.map((product, index) => (
                  <div className="tag-input-select_single_tag_container" key={index}>
                    <p className="tag-text">{product.title}</p>

                    <div className="flex items-center" onClick={() => handleProductRemove(product)}>
                      <FontAwesomeIcon fixedWidth icon={faXmark} className="tag-icon" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <ButtonNew onClick={() => updateFilterModal({ isOpen: true })}>
              <div className="justify-between">
                <FontAwesomeIcon size="lg" icon={faBarsFilter} /> {t(" Filter by")}
              </div>
            </ButtonNew>
          </div>

          <FilterTags filterKeys={filterKeys} filterState={filterDisplayState} onClearFilter={clearSelectedFilter} />
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <ItemSalesHistoryTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />

      <Portal isMounted={filterModal.isOpen}>
        <FilterDrawer
          onSelect={onCheckBoxChange}
          onClose={closeFilterModal}
          onClear={handleClearAllFilters}
          onOk={() => void getReport()}
          filterTitle={t("Departments")}
          isOpen={filterModal.isOpen}
          departmentTree={state?.departmentTree}
          deptIds={[...filterState.department, ...filterState.category, ...filterState.subcategory]}
        />
      </Portal>
    </Page>
  );
}
